import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, RefreshCw, GripVertical, AlertTriangle } from 'lucide-react';
import { adminManagementApi } from '../../services/api';
import { StuiDiagram, Box, Arrow, Note } from '../../components/common/StuiDiagram';
import { usePermissionMap, GridSection } from './permissionMap';

/**
 * Security Overview — how DAWN decides access, for SysAdmins and anyone about to grant something.
 *
 * The diagrams show the mechanism; the tables of keys and the problem count come from the permission
 * map (GET /api/admin/permission-map), which reads what the API enforces, so they cannot go stale.
 */

// ── Diagrams ──────────────────────────────────────────────────────────────────

const LayersDiagram = () => (
  <StuiDiagram title="The Layers" width={820} height={250}
    label="A save needs the page's key, the table's owner key and the verb key the page picks; a utility needs only its own key."
    caption="Access is three layers that must all agree for a save, plus utilities that stand apart. SysAdmins pass every layer.">
    {(arrow) => (<>
      <Box x={20} y={20} w={180} h={64} title="Navigation row" sub="the page: who can open it" sub2="e.g. dataentry.integration" />
      <Box x={220} y={20} w={180} h={64} title="Table owner" sub="the data: AppSec_TableOwner" sub2="e.g. engineering.assets" />
      <Box x={420} y={20} w={180} h={64} title="Action key" sub="the verb the page picks" sub2="e.g. action.dataentry…update" />
      <Box x={620} y={20} w={180} h={64} title="Utility key" sub="a tool's own endpoints" sub2="e.g. integration.iobuffer" tone="muted" />
      <Arrow from={[110, 84]} to={[290, 168]} via={[[110, 130], [290, 130]]} marker={arrow()} />
      <Arrow from={[310, 84]} to={[310, 168]} marker={arrow()} />
      <Arrow from={[510, 84]} to={[330, 168]} via={[[510, 130], [330, 130]]} marker={arrow()} />
      <Note x={410} y={124} anchor="middle">all three</Note>
      <Box x={210} y={168} w={220} h={52} title="A save passes" sub="from that page, to that table" tone="accent" />
      <Arrow from={[710, 84]} to={[710, 168]} marker={arrow('muted')} tone="muted" label="its key alone" labelAt={[716, 130]} anchor="start" />
      <Box x={620} y={168} w={180} h={52} title="The tool runs" sub="DS, IoBuffer, Sync, packages" tone="muted" />
    </>)}
  </StuiDiagram>
);

const GATE_X = [20, 210, 400, 590, 780];
const WritePathDiagram = () => {
  const gates = [
    ['1 Page named', 'X-Nav-Key → nav row', '403: no page named', '(even for a SysAdmin)'],
    ['2 Page covers table', "the module's tables, bulk rules", "403: the page doesn't", 'write this table (even SysAdmin)'],
    ["3 Page's key", "the row's permission key", "403: missing the page's key", 'e.g. dataentry.integration'],
    ['4 Table owner key', 'AppSec_TableOwner', "403: missing the table's", 'owner key'],
    ['5 Verb key', '+ datasheet.update on a sheet', '403: missing the verb key', 'action.{surface}.listview.*'],
  ];
  return (
    <StuiDiagram title="A Save, Gate by Gate" width={980} height={330}
      label="Every save walks five gates in order, then the server drops read-only fields, sets the stamps and writes the audit row."
      caption="One function (DecideWrite) runs these gates for every save, every edit button and the access matrix. A failed gate answers 403 with its reason; a SysAdmin skips gates 3 to 5."
      after={(
        <p>
          Fields: on an engineering page, columns Field Manager marks read-only are dropped; in a data entry module, only
          the module's editable columns are kept; Bulk Import writes every column but the ID. Dropped columns are never an
          error, because pages send whole rows. Stamps: the server sets <code>Rev_Date</code> / <code>Rev_User</code> on
          every save and a status's date and user when the status changes; only a SysAdmin's explicit value is kept.
        </p>
      )}>
      {(arrow) => (<>
        {gates.map(([title, sub, f1, f2], i) => (
          <g key={title}>
            <Box x={GATE_X[i]} y={90} w={160} h={56} title={title} sub={sub} />
            <Arrow from={[GATE_X[i] + 80, 90]} to={[GATE_X[i] + 80, 50]} marker={arrow('fail')} tone="fail" />
            <Note x={GATE_X[i] + 80} y={22} anchor="middle" tone="fail">{f1}</Note>
            <Note x={GATE_X[i] + 80} y={36} anchor="middle" tone="fail">{f2}</Note>
            {i < 4 && <Arrow from={[GATE_X[i] + 160, 118]} to={[GATE_X[i + 1], 118]} marker={arrow('pass')} tone="pass" />}
          </g>
        ))}
        <Arrow from={[290, 146]} to={[860, 190]} via={[[290, 190]]} marker={undefined} tone="accent" dash
          label="SysAdmin: allowed after gate 2" labelAt={[560, 184]} />
        <Arrow from={[860, 146]} to={[860, 250]} marker={arrow('pass')} tone="pass" label="passed" labelAt={[866, 172]} anchor="start" />
        <Box x={780} y={250} w={160} h={56} title="6 Fields" sub="read-only columns dropped" />
        <Box x={590} y={250} w={160} h={56} title="7 Stamps" sub="Rev_ and status dates/users" />
        <Box x={400} y={250} w={160} h={56} title="8 Audit" sub="one AudTrl_CRUD row" />
        <Box x={210} y={250} w={160} h={56} title="Saved" tone="accent" />
        <Arrow from={[780, 278]} to={[750, 278]} marker={arrow()} />
        <Arrow from={[590, 278]} to={[560, 278]} marker={arrow()} />
        <Arrow from={[400, 278]} to={[370, 278]} marker={arrow()} />
      </>)}
    </StuiDiagram>
  );
};

const VerbEdgeDiagram = () => (
  <StuiDiagram title="The Page Picks the Verb" width={820} height={290}
    label="The same table saved from two pages: the owner key is the table's either way, the verb key comes from the page."
    caption="The table decides the owner key; the page decides the verb key. A data entry crew can save Tags with action.dataentry.listview.update without being able to edit every engineering page.">
    {(arrow) => (<>
      <Box x={290} y={6} w={260} h={40} title="action.engineering.listview.update" tone="accent" />
      <Box x={20} y={60} w={200} h={52} title="Engineering page" sub="nav row 'tags'" />
      <Box x={20} y={176} w={200} h={52} title="Data entry module" sub="nav row 'integration'" />
      <Box x={290} y={242} w={260} h={40} title="action.dataentry.listview.update" tone="accent" />
      <Box x={600} y={116} w={200} h={56} title="Table Tags" sub="owner key: engineering.assets" />
      <Arrow from={[120, 60]} to={[290, 26]} via={[[120, 26]]} marker={arrow('accent')} tone="accent" label="picks the verb" labelAt={[200, 20]} />
      <Arrow from={[120, 228]} to={[290, 262]} via={[[120, 262]]} marker={arrow('accent')} tone="accent" label="picks the verb" labelAt={[200, 256]} />
      <Arrow from={[220, 86]} to={[600, 134]} marker={arrow('muted')} tone="muted" label="saves to" labelAt={[420, 100]} />
      <Arrow from={[220, 202]} to={[600, 154]} marker={arrow('muted')} tone="muted" label="saves to" labelAt={[420, 194]} />
      <Note x={700} y={196} anchor="middle" tone="muted">same owner key from both pages</Note>
    </>)}
  </StuiDiagram>
);

const CONSUMERS = ['Sidebar tree', 'Page guard (NavGuard)', 'Edit buttons (capabilities)', 'Every save (/api/dynamic)', 'Reports: list, run, deliveries', 'Access matrix'];
const ResolverDiagram = () => (
  <StuiDiagram title="One Resolver, Every Consumer" width={820} height={280}
    label="One resolver reads grants, navigation rows and table owners, and every consumer asks it."
    caption="Nothing in the browser decides access from a key: the sidebar, the page guards, the edit buttons and the saves all get their answer from the same server code, so they cannot disagree.">
    {(arrow) => (<>
      <Box x={20} y={40} w={180} h={48} title="Grants" sub="roles, overrides, global" />
      <Box x={20} y={116} w={180} h={48} title="Navigation rows" sub="AppCfg_Navigation" />
      <Box x={20} y={192} w={180} h={48} title="Table owners" sub="AppSec_TableOwner" />
      {[64, 140, 216].map(y => <Arrow key={y} from={[200, y]} to={[290, 140]} marker={arrow()} />)}
      <Note x={240} y={70} anchor="middle">reads</Note>
      <Box x={290} y={108} w={240} h={64} title="One resolver" sub="PermissionChecker · NavigationAccess" sub2="ReportAccess" tone="accent" />
      {CONSUMERS.map((c, i) => {
        const y = 8 + i * 46;
        return (
          <g key={c}>
            <Arrow from={[530, 140]} to={[600, y + 18]} marker={arrow('accent')} tone="accent" />
            <Box x={600} y={y} w={200} h={36} title={c} />
          </g>
        );
      })}
      <Note x={410} y={192} anchor="middle" tone="accent">answers every consumer</Note>
    </>)}
  </StuiDiagram>
);

const ReportsDiagram = () => (
  <StuiDiagram title="Reports Are Gated by Their Section" width={820} height={240}
    label="A report row inherits its section header's key, and every report check asks for that key only."
    caption="A report's section is its access. Moving a report to another section header moves who can run it and who keeps getting its deliveries. Its data tables' owner keys are not asked for: a section key grants that data.">
    {(arrow) => (<>
      <Box x={20} y={92} w={200} h={56} title="Section header" sub="reports-general" sub2="key reports.general" tone="accent" />
      {['Drawing Log', 'Panel Report', 'IO Assignments'].map((r, i) => (
        <g key={r}>
          <Arrow from={[220, 120]} to={[290, 34 + i * 76]} marker={arrow()} />
          <Box x={290} y={10 + i * 76} w={200} h={48} title={r} sub="inherits reports.general" />
          <Arrow from={[490, 34 + i * 76]} to={[580, 120]} marker={arrow('accent')} tone="accent" />
        </g>
      ))}
      <Note x={240} y={192} anchor="middle">parent</Note>
      <Box x={580} y={84} w={220} h={72} title="Every report check" sub="sidebar, Reports page, run," sub2="subscriptions, PDF jobs, deliveries" tone="accent" />
      <Note x={690} y={180} anchor="middle" tone="muted">asks for reports.general only;</Note>
      <Note x={690} y={194} anchor="middle" tone="muted">the data tables' owner keys are not checked</Note>
    </>)}
  </StuiDiagram>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const SecurityOverviewPage = () => {
  const [summary, setSummary] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { map, error: mapError, reload } = usePermissionMap();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await adminManagementApi.getSummary();
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verbKeys = map?.keys.filter(k => k.verbs.length > 0) || [];
  const keys = map?.keys || [];
  const code = t => <code>{t}</code>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Shield size={20} />
          <h1>Security Overview</h1>
          <span className="stui-module-header-meta">
            {summary
              ? `${summary.activeUsers} users • ${summary.databases} databases • ${summary.permissions} permissions`
              : ''}
          </span>
        </div>
        <div className="stui-module-header-actions">
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); reload(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-tab-body">
        <p>
          Every capability is an explicit permission key, and a role does nothing but bundle default grants of keys.
          The one exception is the {code('IsSysAdmin')} flag on {code('AppSec_Users')}, which passes every check
          except the two that catch a malformed request. To see what any key opens, or why one person can or cannot save
          on one page: Administration &gt; Security &gt; Permissions (Keys for a key, Matrix for every page against every
          action), or Administration &gt; Users for one person (a database role's eye button).
        </p>
        {mapError && <div className="stui-alert stui-alert-error">{mapError}</div>}
        {map && map.problems.length > 0 && (
          <p className="stui-hint">
            <AlertTriangle size={12} className="stui-text-muted" /> The permission map found {map.problems.length} problem{map.problems.length === 1 ? '' : 's'}:
            see the Problems tab on Administration &gt; Security &gt; Permissions.
          </p>
        )}

        <LayersDiagram />
        <WritePathDiagram />
        <VerbEdgeDiagram />
        <ResolverDiagram />
        <ReportsDiagram />

        {!map && !mapError && <p className="stui-hint">Reading the permission map...</p>}
        {map && (
          <GridSection title={`Keys the Write Check Asks For (${verbKeys.length})`}
            columns={[{ label: 'Key', width: '30%' }, { label: 'What it does' }, { label: 'Role defaults', width: '20%' }]}
            rows={verbKeys.map(k => ({ key: k.key, cells: [code(k.key), k.verbs.join(' '), k.roles.join(', ') || 'None'] }))} />
        )}
        {map && (
          <GridSection title={`Every Key (${keys.length})`}
            columns={[{ label: 'Key', width: '24%' }, { label: 'Opens' }, { label: 'Owns', width: '20%' },
              { label: 'Endpoints', width: '10%' }, { label: 'Role defaults', width: '16%' }]}
            rows={keys.map(k => ({
              key: k.key,
              cells: [
                <span title={k.flags.join(' ') || undefined}>{code(k.key)}{k.flags.length > 0 && <> <AlertTriangle size={12} className="stui-text-muted" /></>}</span>,
                k.navRows.map(n => n.label).join(', ') || '—',
                k.tables.map(t => t.table).join(', ') || '—',
                k.endpoints.length || '—',
                k.roles.join(', ') || '—',
              ],
            }))} />
        )}

        <GridSection title="Entities"
          columns={[{ label: 'Entity', width: '18%' }, { label: 'Table', width: '24%' }, { label: 'What it is' }]}
          rows={[
            ['User', 'AppSec_Users', <>A person, signed in with Entra ID. {code('IsSysAdmin')} is the only bypass.</>],
            ['Database', 'AppSec_Databases', <>A project database. Its {code('Slug')} keys roles and per-database grants.</>],
            ['Role', 'AppSec_UserRoles', 'A person\'s role on one database: Guest, User, PowerUser or AppAdmin. It only bundles role defaults.'],
            ['Permission key', 'AppSec_DefPermissions', <>{code('<section>.<feature>')}, with a GrantScope of Global, Instance or Both.</>],
            ['Role default', 'AppSec_DefRoles', 'Grants a role carries. Grant only.'],
            ['Override', 'AppSec_UserPermissions', 'A Grant or Deny for one person on one database.'],
            ['Global grant / deny', 'AppSec_GlobalPermissions', 'A Grant or Deny for one person in every database. A global Deny is a veto.'],
            ['Table owner', 'AppSec_TableOwner', 'Every project-database table\'s owner key and category. No row: only a SysAdmin can read or write it.'],
            ['Navigation row', 'AppCfg_Navigation', 'A sidebar entry and the page it opens. Its key opens the page and is the first gate on every save from it.'],
          ].map(([e, t, w]) => ({ key: e, cells: [e, code(t), w] }))} />

        <h2 className="stui-detail-section-header">How a Key Resolves</h2>
        <p>Is a key held, on a database? First match wins:</p>
        <ol>
          <li>SysAdmin: yes, always.</li>
          <li>A global Deny: no, in every database. Nothing below wins it back.</li>
          <li>A global Grant: yes, even over an override Deny on one database.</li>
          <li>An override on this database: its Grant or Deny.</li>
          <li>A role default for the person's role on this database: yes.</li>
          <li>Otherwise: no.</li>
        </ol>
        <p>
          To see it for a real person: Administration &gt; Users, pick the person and a database role, then the eye button.
          Keys held shows each key with the rung that decided it; Access shows every page against every action with what
          they would need. The same code that answers every request builds both.
        </p>

        <GridSection title="Before You Grant"
          columns={[{ label: 'Rule', width: '30%' }, { label: 'Why' }]}
          rows={[
            ['Prefer role defaults.', <>A key everyone in a role needs belongs in {code('AppSec_DefRoles')}; per-person grants are for exceptions.</>],
            ['Grant the page, not the verb.', 'A data entry crew needs the module\'s key and the data entry verb key. An engineering verb key opens every engineering page\'s edits.'],
            ['Global grants for global keys.', 'Use global grants for keys that are not about one database. A global Deny removes the key everywhere, including databases you are not looking at.'],
            ['Never delete or renumber a key.', 'Grants, owners and navigation rows reference the key\'s ID, so a delete fails while anything uses it. A key is never renamed either: code and attributes name it, so a new name is a code change.'],
            ['Give a new table an owner before it is used.', 'Without an owner, only a SysAdmin can read or write it. The Problems tab on Permissions lists tables that pages write and nobody owns.'],
          ].map(([r, w]) => ({ key: r, cells: [r, w] }))} />

        <GridSection title="Where to Make Changes"
          columns={[{ label: 'Change', width: '36%' }, { label: 'Where' }]}
          rows={[
            ['What a key opens, and what each page takes', 'Administration > Security > Permissions: the Keys and Matrix tabs'],
            ['What one person holds, can and cannot do, and what they would need', 'Administration > Users: pick the person and a database role, then the eye button (Keys held, Access)'],
            ['How each API endpoint is guarded', 'Administration > Security > Permissions: action menu, How endpoints are guarded'],
            ['Add or describe a key', 'Administration > Security > Permissions: the Keys tab'],
            ['Grants a role carries', 'Administration > Security > Permission Defaults'],
            ['A person\'s roles, overrides and global grants', 'Administration > Users (the grids under the grant grids show what the key opens)'],
            ['A table\'s owner', 'Administration > Security > Table Ownership'],
            ['The sidebar: rows, keys, sections, order, icons', 'Development > Configuration > Navigation. A new page also needs its route in the app; an engineering table, data entry module or report needs only its row.'],
            ['Move a report to another section', <>Development &gt; Configuration &gt; Navigation: give its row another {code('reports-*')} parent.</>],
          ].map(([c, w]) => ({ key: c, cells: [c, w] }))} />

        <GridSection title="Special Cases"
          columns={[{ label: 'Situation', width: '30%' }, { label: 'Behavior' }]}
          rows={[
            [<>{code('engineering.supportingdoc')} and {code('.view')}</>, 'Either key reads the supporting-document tables; only the base key saves to them.', 'sd'],
            ['Datasheet saves', <>Need the page's {code('listview.update')} key and its {code('datasheet.update')} key.</>, 'ds'],
            ['Bulk delete', <>Needs {code('action.bulkdelete')}, the owner key, and a page whose navigation row allows it. Never from a data entry module.</>, 'bd'],
            ['Data entry modules on engineering tables', 'The table\'s owner key is still required. A crew with only a module key cannot save a table they cannot read.', 'de'],
            ['No table owners at all', <>An empty {code('AppSec_TableOwner')} allows every read and write (a new environment's first deploy). One row turns the check on, table by table.</>, 'no'],
            ['Impersonation', 'A SysAdmin\'s preview: the server sends the impersonated person\'s tree and edit buttons, while every request still runs as the SysAdmin.', 'im'],
          ].map(([c, w, k]) => ({ key: k, cells: [c, w] }))} />
      </div>
    </div>
  );
};

export default SecurityOverviewPage;
